import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Stack, Typography, keyframes } from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import useSidemenuStore from '@/app/sidemenu.store';
import {
  AUTO_DISMISS_MS,
  MAX_VISIBLE,
  SEVERITY_ACCENT_COLORS,
  ToastNotification,
  getMonitorCategoryLabel,
  getNotificationIcon,
  getNotificationLink,
  notificationBodyContext,
  useNotificationToastsStore,
  useNotificationsStore,
  weTransferNotificationValues,
} from '@/features/notifications';
import { useOrdersService } from '@/features/orders';
import { queryKeys } from '@/react-query/constants';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  0% {
    transform: translateX(0);
    max-height: 5rem;
    margin-bottom: 0.5rem;
    opacity: 1;
  }
  70% {
    transform: translateX(-110%);
    max-height: 5rem;
    margin-bottom: 0.5rem;
    opacity: 1;
  }
  100% {
    transform: translateX(-110%);
    max-height: 0;
    margin-bottom: 0;
    opacity: 0;
  }
`;

const ToastCard: FC<{
  toast: ToastNotification;
  onDismiss: (id: string) => void;
  exiting: boolean;
}> = ({ toast, onDismiss, exiting }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userType = useContext(UserTypeContext);
  const { getOrders } = useOrdersService();

  const { data: pendingOrdersCount = 0 } = useQuery({
    queryKey: [queryKeys.orders, 'pending-count'],
    queryFn: async ({ signal }) => {
      const response = await getOrders(
        { request: { pageNumber: 0, numberOfItems: 1, status: ['PENDING'] } },
        signal,
      );
      return response.totalElements ?? 0;
    },
    enabled: userType !== 'service',
  });
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const { notification } = toast;
  const accentColor = SEVERITY_ACCENT_COLORS[notification.severity ?? 'COMMON'];

  const detailsObj = (() => {
    try {
      return JSON.parse(notification.audit.details ?? '{}') as Record<string, unknown>;
    } catch {
      return {};
    }
  })();

  const extraDetailsObj = (() => {
    const raw = notification.audit.extraDetails;
    if (!raw) return {};
    let fixed = raw;
    if (fixed.startsWith('"') && fixed.endsWith('"')) {
      fixed = fixed.substring(1, fixed.length - 1);
    }
    fixed = fixed.replace(/\\(.)/g, '$1');
    try {
      const parsed = JSON.parse(fixed);
      return Array.isArray(parsed) ? {} : ((parsed as Record<string, unknown>) ?? {});
    } catch {
      return {};
    }
  })();

  const notificationType = notification.audit.type;
  const derivedValues: Record<string, unknown> = {};

  if (
    [
      'USER_IMPERSONATED',
      'USER_CREATED',
      'USER_UPDATED',
      'USER_AVATAR_UPDATED',
      'USER_DISABLED',
      'USER_ENABLED',
    ].includes(notificationType)
  ) {
    derivedValues.userName = `${detailsObj?.firstName ?? ''} ${detailsObj?.lastName ?? ''}`.trim();
  } else if (notificationType === 'ACCESS_REQUESTED') {
    derivedValues.userName = notification.audit.userName;
  }

  if (['CLIENT_CREATED', 'CLIENT_UPDATED', 'CLIENT_DELETED'].includes(notificationType)) {
    derivedValues.clientName = detailsObj?.companyName;
  }

  if (
    notificationType.startsWith('LEASING_CLIENT_') ||
    notificationType.startsWith('KIOSK_LEASING_')
  ) {
    const leasingClient = detailsObj?.leasingClient as Record<string, unknown> | undefined;
    derivedValues.leasingClientName = leasingClient?.name ?? '-';
  }

  if (['SERVICE_CREATED', 'SERVICE_UPDATED', 'SERVICE_DELETED'].includes(notificationType)) {
    derivedValues.serviceName = detailsObj?.materialName ?? '-';
  }

  if (notificationType.startsWith('SPACE_')) {
    derivedValues.spaceName = `${detailsObj?.supportName || ''} - ${
      (detailsObj?.locationEntity as Record<string, unknown>)?.locationName || ''
    }`;
  }

  if (notificationType.startsWith('ADVERTISING_PACKAGE_')) {
    derivedValues.advertisingPackageName = detailsObj?.packageName ?? '-';
  }

  const monitorCategoryLabel = getMonitorCategoryLabel(notificationType, extraDetailsObj);
  if (monitorCategoryLabel !== undefined) {
    derivedValues.category = monitorCategoryLabel;
  }

  const translationValues = {
    ...detailsObj,
    ...extraDetailsObj,
    pendingOrdersCount,
    ...derivedValues,
    ...weTransferNotificationValues(notificationType, detailsObj, t),
  };

  const title = t(`notifications.title.${notification.audit.type}`, translationValues);
  const rawDescription = t(`notifications.body.${notification.audit.type}`, {
    ...translationValues,
    defaultValue: '',
    context: notificationBodyContext(detailsObj, extraDetailsObj),
  });
  const description = rawDescription.replace(/<[^>]*>/g, '').trim();
  const time = dayjs(notification.audit.timestamp).format('HH:mm');

  const startTimer = useCallback(() => {
    timerRef.current = setTimeout(() => {
      onDismiss(toast.id);
    }, AUTO_DISMISS_MS);
  }, [onDismiss, toast.id]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    startTimer();
    return clearTimer;
  }, [startTimer, clearTimer]);

  const handleMouseEnter = () => {
    setIsHovered(true);
    clearTimer();
  };

  const notificationLink = getNotificationLink({
    type: notification.audit.type,
    details: notification.audit.details,
    extraDetails: notification.audit.extraDetails,
    userType,
  });

  const handleMouseLeave = () => {
    setIsHovered(false);
    startTimer();
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="0.625rem"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        bgcolor: '#fefefe',
        boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
        borderRadius: '0.625rem',
        height: '3.8125rem',
        pr: '0.5rem',
        py: '0.5rem',
        flexShrink: 0,
        width: '100%',
        animation: `${exiting ? slideOut : slideIn} ${exiting ? '1.4s' : '0.35s'} cubic-bezier(0.4, 0, 0.2, 1) forwards`,
      }}
    >
      {isHovered && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
          }}
          sx={{
            position: 'absolute',
            top: '-0.5rem',
            right: '-0.5rem',
            width: '1.5rem',
            height: '1.5rem',
            borderRadius: '9999px',
            bgcolor: '#fefefe',
            boxShadow: '0px 1px 4px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 1,
          }}
        >
          <CloseIcon sx={{ fontSize: '1rem', color: '#615e6f' }} />
        </Box>
      )}

      <Box
        sx={{
          width: '0.25rem',
          height: '2.739rem',
          bgcolor: accentColor,
          borderRadius: '0 6.25rem 6.25rem 0',
          flexShrink: 0,
        }}
      />

      <Box sx={{ flexShrink: 0, display: 'flex', color: '#807D93' }}>
        {getNotificationIcon(notification.audit.type)}
      </Box>

      <Stack flex={1} minWidth={0} justifyContent="center" height="2.8125rem">
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            noWrap
            sx={{
              fontSize: '0.75rem',
              color: '#615e6f',
              fontFamily: 'Neue Frutiger World',
              fontWeight: 400,
              lineHeight: 1.6,
              flex: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: '#615e6f',
              fontFamily: 'Neue Frutiger World',
              fontWeight: 400,
              lineHeight: 1.6,
              flexShrink: 0,
              ml: '0.5rem',
              whiteSpace: 'nowrap',
            }}
          >
            {time}
          </Typography>
        </Stack>

        <Typography
          noWrap
          sx={{
            fontSize: '0.625rem',
            color: '#615e6f',
            fontFamily: 'Neue Frutiger World',
            fontWeight: 400,
            lineHeight: 1.6,
            alignSelf: 'stretch',
          }}
        >
          {notificationLink
            ? description.split(/(#\S+)/).map((part, i) =>
                part.startsWith('#') ? (
                  <Box
                    key={i}
                    component="span"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(notificationLink);
                      onDismiss(toast.id);
                    }}
                    sx={{
                      textDecoration: 'underline',
                      cursor: 'pointer',
                    }}
                  >
                    {part}
                  </Box>
                ) : (
                  part
                ),
              )
            : description}
        </Typography>
      </Stack>
    </Stack>
  );
};

const NotificationToastContainer: FC = () => {
  const toasts = useNotificationToastsStore((s) => s.toasts);
  const removeToast = useNotificationToastsStore((s) => s.removeToast);
  const clearAll = useNotificationToastsStore((s) => s.clearAll);
  const sidebarWidth = useSidemenuStore((s) => s.sidebarWidth);
  const setIsDrawerOpen = useNotificationsStore((s) => s.setIsDrawerOpen);
  const { t } = useTranslation();
  const isDrawerOpen = useNotificationsStore((s) => s.isDrawerOpen);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  const handleOpenDrawer = useCallback(() => {
    setIsDrawerOpen(true);
    clearAll();
  }, [setIsDrawerOpen, clearAll]);

  const handleDismiss = useCallback(
    (id: string) => {
      setExitingIds((prev) => new Set(prev).add(id));
      setTimeout(() => {
        removeToast(id);
        setExitingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, 1400);
    },
    [removeToast],
  );

  if (toasts.length === 0 || isDrawerOpen) return null;

  const visibleToasts = toasts.slice(-MAX_VISIBLE);
  const hiddenCount = Math.max(0, toasts.length - MAX_VISIBLE);

  return (
    <Box
      sx={{
        position: 'fixed',
        left: `${sidebarWidth}px`,
        right: 0,
        top: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 1300,
      }}
    >
      <Stack
        sx={{
          position: 'absolute',
          bottom: '1rem',
          left: '2rem',
          width: '22rem',
          pointerEvents: 'auto',
          gap: '0.25rem',
        }}
      >
        {visibleToasts.map((toast) => (
          <ToastCard
            key={toast.id}
            toast={toast}
            onDismiss={handleDismiss}
            exiting={exitingIds.has(toast.id)}
          />
        ))}
        {hiddenCount > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            onClick={handleOpenDrawer}
            sx={{
              bgcolor: '#fefefe',
              borderRadius: '0.625rem',
              boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
              height: '1.8125rem',
              px: '0.5rem',
              cursor: 'pointer',
              animation: `${slideIn} 0.35s ease forwards`,
            }}
          >
            <Stack direction="row" alignItems="center" gap="0.375rem">
              <Box
                sx={{
                  bgcolor: '#ebebee',
                  borderRadius: '6.25rem',
                  px: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: '#737085',
                    fontFamily: 'Neue Frutiger World',
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  +{hiddenCount}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: '#615e6f',
                  fontFamily: 'Neue Frutiger World',
                  fontWeight: 400,
                  lineHeight: 1.6,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('more_notifications', { defaultValue: 'more notifications' })}
              </Typography>
            </Stack>
            <ChevronRightIcon sx={{ fontSize: '1.125rem', color: '#737085' }} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default NotificationToastContainer;
